/**
 * Reports API
 * Operations cost analysis and dashboard endpoints
 */

import apiClient from './client';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

// ============================================================
// Types
// ============================================================

export interface CostConfig {
  exchange_rate: number;
  member_price: number;
}

export interface UserChatStats {
  user_id: string;
  name: string | null;
  phone: string | null;
  gender: string | null;
  session_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_tokens: number;
  avg_duration: number;
  first_session: string;
  last_session: string;
  lifecycle_days: number;
  chat_cost_cny: number;
  daily_service_cost: number;
  total_cost_cny: number;
}

export interface DailyStats {
  date: string;
  sessions: number;
  users: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  chat_cost_cny: number;
}

export interface SoulmateStats {
  type: 'lifetime' | 'freestyle';
  count: number;
  input_tokens: number;
  output_tokens: number;
  cost_cny: number;
}

export interface TrialAnalysis {
  user_id: string;
  sessions_in_5_days: number;
  lifecycle_days: number;
  retained: boolean;
}

export interface CostSummary {
  total_users: number;
  total_sessions: number;
  total_chat_cost_cny: number;
  total_daily_service_cost_cny: number;
  total_soulmate_cost_cny: number;
  total_cost_cny: number;
  avg_cost_per_user_cny: number;
  median_cost_cny: number;
  cost_percentiles: Record<string, number>;
  profitable_users: number;
  profitable_rate: number;
}

export interface CostTier {
  label: string;
  min_cost: number;
  max_cost: number;
  user_count: number;
  percentage: number;
  avg_cost: number;
  covered_by_membership: boolean;
  profit_per_user: number;
}

export type RefundRateSource = 'observed' | 'override' | 'observed_unavailable' | 'observed_out_of_range';
export type IncludedServicePlane = 'full' | 'lite' | 'none';

export interface PricingScenario {
  id: string;
  name: string;
  price: number;
  plan_price_cny: number;
  daily_service_type: IncludedServicePlane;
  included_service_plane: IncludedServicePlane;
  refund_rate: number;
  refund_rate_source: RefundRateSource;
  refund_rate_override: number | null;
  description: string;
  notes: string;
  active: boolean;
  sort_order: number;
  breakeven_paying_users: number;
  breakeven_gap: number;
}

export interface PricingRealPnl {
  window_days: number;
  gross_revenue_cny: number;
  refund_cny: number;
  net_revenue_cny: number;
  total_ledger_cost_cny: number;
  monthly_ledger_cost_cny: number;
  real_profit_cny: number;
  profit_margin: number;
  paying_users: number;
  active_subscriptions: number;
}

export interface PricingMetricCohort {
  included_users: string;
  exclude_team: boolean;
  user_filter: string;
  calculation_basis: string;
}

export interface PricingCohortContract {
  cost: PricingMetricCohort;
  revenue: PricingMetricCohort;
  refund: PricingMetricCohort;
  cohorts_match: boolean;
}

export interface PricingObservedRevenue {
  gross_revenue_cny_cents: number;
  refund_cny_cents: number;
  net_revenue_cny_cents: number;
  gross_revenue_cny: number;
  refund_cny: number;
  net_revenue_cny: number;
  observed_refund_rate: number | null;
  transaction_count: number;
  refund_count: number;
  paying_users: number;
  date_attribution: 'gross_purchase_date_refund_refunded_at' | string;
}

export interface PricingContext {
  window_days: number;
  month_factor: number;
  sample_size: number;
  sample_quality: string;
  cohort_contract: PricingCohortContract;
  observed_revenue: PricingObservedRevenue;
  real_pnl: PricingRealPnl;
}

export interface ReportWarning {
  code: string;
  severity: string;
  message: string;
}

export interface ReportsDashboard {
  timestamp: string;
  date_range: {
    start: string;
    end: string;
  };
  config: CostConfig;
  summary: CostSummary;
  users: UserChatStats[];
  daily_stats: DailyStats[];
  soulmate_stats: SoulmateStats[];
  trial_analysis: TrialAnalysis[];
  cost_tiers: CostTier[];
  pricing_scenarios: PricingScenario[];
  pricing_context: PricingContext;
  warnings: ReportWarning[];
}

export interface RevenueOverview {
  total_revenue_cny_cents: number;
  total_transactions: number;
  paying_users: number;
  arpu_cny_cents: number;
  by_channel: Record<string, number>;
  by_product: Record<string, number>;
  refund_cny_cents: number;
  refund_count: number;
  active_subscriptions: number;
  trial_users: number;
}

export interface DailyRevenue {
  date: string;
  total_cny_cents: number;
  apple_cny_cents: number;
  alipay_cny_cents: number;
  transaction_count: number;
  refund_cny_cents: number;
  net_cny_cents: number;
}

export interface ConversionFunnel {
  registered: number;
  trialed: number;
  purchased: number;
  renewed: number;
  churned: number;
}

export interface TrialConversionDay {
  day: number;
  converted_count: number;
}

export interface CohortRetention {
  cohort_month: string;
  cohort_size: number;
  retained: Record<number, number>;
}

export interface ExpiringUsers {
  within_3_days: number;
  within_7_days: number;
  within_14_days: number;
}

export interface ProductMigration {
  from_product: string;
  to_product: string;
  user_count: number;
}

export interface RevenueAnalyticsResponse {
  overview: RevenueOverview;
  daily_revenue: DailyRevenue[];
  funnel: ConversionFunnel;
  trial_conversion_distribution: TrialConversionDay[];
  cohort_retention: CohortRetention[];
  expiring_users: ExpiringUsers;
  product_migrations: ProductMigration[];
}

export interface PnlTransaction {
  channel: string;
  type: string;
  product_id: string;
  count: number;
  amount_cny_cents: number;
}

export interface PnlMonthly {
  month: string;
  gross_revenue_cents: number;
  apple_revenue_cents: number;
  alipay_revenue_cents: number;
  apple_revenue_pre_cents?: number;
  apple_revenue_post_cents?: number;
  apple_refund_cents: number;
  alipay_refund_cents: number;
  apple_refund_pre_cents?: number;
  apple_refund_post_cents?: number;
  refund_cents: number;
  apple_commission_cents: number;
  net_revenue_cents: number;
  total_cost_cny: number;
  gross_profit_cny: number;
  transactions: PnlTransaction[];
}

export interface PnlTotals {
  gross_revenue_cents: number;
  refund_cents: number;
  apple_commission_cents: number;
  net_revenue_cents: number;
  total_cost_cny: number;
  gross_profit_cny: number;
}

export interface AppleCommissionConfig {
  cutover_date: string;
  rate_before: number;
  rate_after: number;
}

export interface PnlSummaryResponse {
  monthly: PnlMonthly[];
  totals: PnlTotals;
  apple_commission_rate: number;
  apple_commission_config?: AppleCommissionConfig;
}

// ============================================================
// API Functions
// ============================================================

export const reportsApi = {
  /**
   * Get complete reports dashboard data
   */
  getDashboard: async (
    startDate?: string,
    endDate?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM,
  ): Promise<ReportsDashboard> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('exclude_team', String(excludeTeam));

    const url = params.toString() ? `/reports/dashboard?${params}` : '/reports/dashboard';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get user chat statistics
   */
  getUserStats: async (
    startDate?: string,
    endDate?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM,
  ): Promise<UserChatStats[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('exclude_team', String(excludeTeam));

    const url = params.toString() ? `/reports/users?${params}` : '/reports/users';
    const response = await apiClient.get(url);
    return response.data;
  },

  /**
   * Get daily usage statistics
   */
  getDailyStats: async (
    startDate?: string,
    endDate?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM,
  ): Promise<DailyStats[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('exclude_team', String(excludeTeam));

    const url = params.toString() ? `/reports/daily?${params}` : '/reports/daily';
    const response = await apiClient.get(url);
    return response.data;
  },

  getRevenueAnalytics: async (
    startDate?: string,
    endDate?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM,
  ): Promise<RevenueAnalyticsResponse> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('exclude_team', String(excludeTeam));
    const response = await apiClient.get(`/reports/revenue?${params.toString()}`);
    return response.data;
  },

  getPnlSummary: async (
    startDate?: string,
    endDate?: string,
    excludeTeam = DEFAULT_EXCLUDE_TEAM,
  ): Promise<PnlSummaryResponse> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('exclude_team', String(excludeTeam));
    const response = await apiClient.get(`/reports/pnl?${params.toString()}`);
    return response.data;
  },
};
